import { useEffect } from 'react'

import { SITE_PUBLIC } from '../constants'

// og:image de repli — URL ABSOLUE obligatoire (un partage de lien ne résout pas les
// chemins relatifs), donc construite depuis le domaine servi. Un littéral ici pointait
// terobs.fr, un domaine sans DNS : le partage mourait en silence, sans rien casser.
const DEFAULT_OG_IMAGE = `${SITE_PUBLIC}/og-image.png`

interface PageMeta {
  title: string
  description?: string
  ogImage?: string
  ogType?: 'website' | 'article' | 'product'
  canonical?: string  // si omis, on prend origin + pathname (JAMAIS la query string)
  /** Empêche l'indexation de la page (admin, /espace, etc.) */
  noindex?: boolean
  /** Suivre les liens d'une page non indexée (`noindex, follow`) — page d'un lien partagé
      (CDC E2 § 7.2) : elle n'est pas indexée, mais son lien vers la page publique l'est. */
  suivreLiens?: boolean
}

function setMetaContent(selector: string, content: string) {
  const el = document.head.querySelector<HTMLMetaElement>(selector)
  if (el) {
    el.setAttribute('content', content)
  } else {
    const newEl = document.createElement('meta')
    // selector ressemble à 'meta[property="og:title"]' ou 'meta[name="description"]'
    const m = selector.match(/^meta\[(name|property)="([^"]+)"\]$/)
    if (!m) return
    newEl.setAttribute(m[1], m[2])
    newEl.setAttribute('content', content)
    document.head.appendChild(newEl)
  }
}

function setLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`)
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

/**
 * Patch <head> dynamiquement par page pour le SEO.
 *
 * Pourquoi pas react-helmet-async ? Dépendance externe non maintenue (2024+) ;
 * 1 hook de 40 lignes suffit pour notre cas (Googlebot moderne exécute le JS).
 *
 * Pour un SSG vrai (Puppeteer prerender, vraies meta dans le HTML servi), voir Sprint 8.
 */
export function usePageMeta(meta: PageMeta) {
  useEffect(() => {
    const previousTitle = document.title
    // On mémorise aussi `robots` pour le restaurer au démontage, symétriquement au titre.
    // Sans ça, un composant qui a posé `noindex,nofollow` laisse la balise derrière lui : dans une
    // transition SPA vers une page valide, l'URL valide porte encore `noindex` jusqu'à ce que la
    // page suivante appelle ce hook — et un crawler qui capture tôt la classe non indexable.
    // (P1 de la revue croisée codex, corrigé ici plutôt que chez un seul appelant : le défaut
    // vaut pour toutes les pages en noindex — /admin, /espace, la 404, l'Observatoire.)
    const previousRobots = document.head
      .querySelector<HTMLMetaElement>('meta[name="robots"]')?.getAttribute('content') ?? null
    document.title = meta.title
    if (meta.description) {
      setMetaContent('meta[name="description"]', meta.description)
      setMetaContent('meta[property="og:description"]', meta.description)
      setMetaContent('meta[name="twitter:description"]', meta.description)
    }
    setMetaContent('meta[property="og:title"]', meta.title)
    setMetaContent('meta[name="twitter:title"]', meta.title)
    setMetaContent('meta[property="og:type"]', meta.ogType || 'website')
    setMetaContent('meta[property="og:image"]', meta.ogImage || DEFAULT_OG_IMAGE)
    setMetaContent('meta[name="twitter:image"]', meta.ogImage || DEFAULT_OG_IMAGE)

    // Canonical/og:url par défaut = origin + pathname, SANS query string ni hash. Deux
    // raisons : (1) une URL canonique ne doit jamais porter de paramètres volatils (utm_…,
    // et surtout `?__prerender=1` que le pré-rendeur ajoute pour forcer le shell frais —
    // voir deploy/prerender.sh + frontend/Caddyfile) ; (2) éviter de fragmenter le signal
    // SEO entre variantes de query pointant vers la même page.
    const url = meta.canonical
      || (typeof window !== 'undefined' ? window.location.origin + window.location.pathname : '')
    if (url) {
      setMetaContent('meta[property="og:url"]', url)
      setLink('canonical', url)
    }

    // robots noindex pour les zones authentifiées (admin, espace client)
    if (meta.noindex) {
      setMetaContent('meta[name="robots"]', meta.suivreLiens ? 'noindex, follow' : 'noindex,nofollow')
    } else {
      setMetaContent('meta[name="robots"]', 'index,follow')
    }

    return () => {
      document.title = previousTitle
      if (previousRobots !== null) {
        setMetaContent('meta[name="robots"]', previousRobots)
      } else {
        // Aucune balise robots avant nous : on retire la nôtre au lieu de laisser un `noindex`
        // orphelin. `index,follow` est de toute façon le comportement par défaut d'un crawler.
        document.head.querySelector('meta[name="robots"]')?.remove()
      }
    }
  }, [meta.title, meta.description, meta.ogImage, meta.canonical, meta.noindex, meta.ogType,
      meta.suivreLiens])
}
